import { signal, computed, ResourceRef, effect } from "@angular/core";
import { AccordionStateToolTip } from "../components/info-page-accordion/info-page-accordion.component";
import { HttpErrorResponse } from "@angular/common/http";

export class AccordionStateClass<T> {
    public visible = signal<boolean>(false);
    public isLoading = computed<boolean>(() => this.ref()?.isLoading() ?? false);
    public hasInfo = computed<boolean>(() => this.ref()?.hasValue() && this.isEmpty(this.ref().value()));
    public hasError = computed<boolean>(() => this.ref()?.error() != null);
    public title = signal<string>("");
    public infotext = signal<string>("");
    public errortext = signal<string>(null);
    public expanded = signal<boolean>(false);
    public tooltip = signal<AccordionStateToolTip>(null);
    public lastUpdated = signal<string>(null);
    public section = signal<AccordionIdentity>(null);
    public data = computed(() => (this.ref()?.hasValue() ? this.ref()?.value() : null));
    private ref = signal<ResourceRef<T>>(undefined);
    private counterOverrideFn: ((input: T) => number) | null = null;

    public counter = computed<number | null>(() => {
        let data = this.ref()?.hasValue() ? this.ref()?.value() : null;
        if (this.counterOverrideFn) {
            return this.counterOverrideFn(data);
        }
        if (data == null || !Array.isArray(data)) {
            return null;
        }

        return (data as Array<unknown>).length;
    });

    /// <summary>
    /// Constructor for AccordionStateClass based on a ResourceRef<T>
    /// <typeparam name="T">Type of data returned by the ResourceRef</typeparam>
    /// <param name="resource">The ResourceRef to base the state on</param>
    /// <param name="title">Title of the accordion</param>
    /// <param name="infoText">Optional information text to display if there is no data, if you don't want default info</param>
    /// <param name="errorText">Optional error text to display, if you don't want default error</param>
    /// <param name="lazyLoadTrigger">Optional callback emitting current expanded state, intended for lazy loading data when accordion is expanded</param>
    /// <param name="tooltip">Optional tooltip data for the accordion header</param>
    /// <param name="counterOverride">Optional override for the counter value, can be a number or a function that takes the data as input and returns a number</param>
    /// </summary>
    constructor(
        resource: ResourceRef<T>,
        title: string,
        section: AccordionIdentity,
        infoText?: string,
        errorText?: string,
        lazyLoadTrigger?: (expanded: boolean) => void, // optional callback emitting current expanded state
        tooltip?: AccordionStateToolTip,
        counterOverride?: number | ((input: T) => number) | null
    ) {
        if (typeof counterOverride === "number") {
            this.counterOverrideFn = () => counterOverride;
        } else {
            this.counterOverrideFn = counterOverride ?? null;
        }
        this.ref.set(resource);
        this.title.set(title);
        this.infotext.set(infoText);
        this.errortext.set(errorText);
        this.section.set(section);
        // If a callback is supplied, emit on any expanded state change, intended to be used to trigger lazy loading of data
        if (lazyLoadTrigger) {
            effect(() => {
                if (this.expanded()) lazyLoadTrigger(this.expanded());
            });
        }
        this.tooltip.set(tooltip);
    }

    private isEmpty(data: T | null) {
        if (data == null) return true;
        if (Array.isArray(data)) return (data as Array<unknown>).length === 0;
        return false;
    }

    expanderClicked(): void {
        this.expanded.update((v) => !v);
    }
    open(): void {
        this.expanded.set(true);
    }
}
export enum AccordionIdentity {
    KeyInfo = "KeyInfoAccordion",
    PhysicalRisk = "PhysicalRiskAccordion",
    Buildings = "BuildingsAccordion",
    ShareSales = "ShareSalesAccordion",
    SpecialConstraints = "SpecialConstraintsAccordion",
    Energy = "EnergyAccordion",
    SubAccordion = "SubAccordion",
    BusinessManager = "BusinessManager",
    ValueHistory = "ValueHistory",
    NearbySales = "NearbySalesAccordion",
    NearbySiteSales = "NearbySiteSalesAccordion",
    PublicAddressInfo = "PublicAddressInfoAccordion",
    Shares = "SharesAccordion",
    AppraisalHistory = "AppraisalHistoryAccordion",
}
